import { SearchState } from "react-instantsearch-core"

import { AllCategories } from "~/hooks/useCategories"
import {
  getCategoriesFacetsBySlug,
  getCategoryById,
} from "~/utils/createPages/categoriesUtils"

const ENCODED_CATEGORIES: { [key: string]: string } = {
  p: "page",
  v: "refinementList.vertical",
  i: 'refinementList["star_ingredients.slug"]',
  t: "refinementList.recipe_type",
  c: "hierarchicalMenu.categories_tree",
  d: "refinementList.difficulty",
  s: 'refinementList.["suitable_for_diets.slug"]',
}

type ParseReturn = {
  hashPart: string
  mainPart?: string
}

const getDietPart = (
  state: SearchState,
  mainParameter = false
): ParseReturn => {
  const hashPart = []
  let mainPart = ""

  if (state.refinementList) {
    //No SSG
    if (
      state.refinementList["suitable_for_diets.slug"] &&
      state.refinementList["suitable_for_diets.slug"].length > 0
    ) {
      if (mainParameter) {
        mainPart += state.refinementList["suitable_for_diets.slug"][0] + "/"
      }
      for (
        let i = mainParameter ? 1 : 0;
        i < state.refinementList["suitable_for_diets.slug"].length;
        i++
      ) {
        hashPart.push(`s-${state.refinementList["suitable_for_diets.slug"][i]}`)
      }
    }
  } else if (
    state.facetsRefinements &&
    state.facetsRefinements["suitable_for_diets.slug"]?.length
  ) {
    //SSG
    //Only main part because hash are not in ssg
    mainPart = state.facetsRefinements["suitable_for_diets.slug"][0] + "/"
  }

  const result: ParseReturn = { hashPart: "" }
  if (hashPart.length > 0) {
    result.hashPart = hashPart.join(",")
  }
  if (mainParameter) {
    result.mainPart = mainPart
  }
  return result
}

const getTypePart = (
  state: SearchState,
  mainParameter = false
): ParseReturn => {
  const hashPart = []
  let mainPart = ""

  if (state.refinementList) {
    if (
      state.refinementList.recipe_type &&
      state.refinementList.recipe_type.length > 0
    ) {
      if (mainParameter) {
        mainPart += state.refinementList.recipe_type[0] + "/"
      }
      for (
        let i = mainParameter ? 1 : 0;
        i < state.refinementList.recipe_type.length;
        i++
      ) {
        hashPart.push(`t-${state.refinementList.recipe_type[i]}`)
      }
    }
  }

  const result: ParseReturn = { hashPart: "" }
  if (hashPart.length > 0) {
    result.hashPart = hashPart.join(",")
  }
  if (mainParameter) {
    result.mainPart = mainPart
  }
  return result
}

const getVerticalPart = (
  state: SearchState,
  mainParameter = false
): ParseReturn => {
  const hashPart = []
  let mainPart = ""

  if (state.refinementList) {
    //No SSG
    if (
      state.refinementList.vertical &&
      state.refinementList.vertical.length > 0
    ) {
      if (mainParameter) {
        mainPart += state.refinementList.vertical[0] + "/"
      }
      for (
        let i = mainParameter ? 1 : 0;
        i < state.refinementList.vertical.length;
        i++
      ) {
        hashPart.push(`v-${state.refinementList.vertical[i]}`)
      }
    }
  } else if (state.facetsRefinements?.vertical?.length) {
    //SSG
    //Only main part because hash are not in ssg
    mainPart = state.facetsRefinements?.vertical[0] + "/"
  }

  const result: ParseReturn = { hashPart: "" }
  if (hashPart.length > 0) {
    result.hashPart = hashPart.join(",")
  }
  if (mainParameter) {
    result.mainPart = mainPart
  }
  return result
}

const getIngredientsPart = (
  state: SearchState,
  mainParameter = false
): ParseReturn => {
  const hashPart = []
  let mainPart = ""

  if (state.refinementList) {
    //No SSG
    if (
      state.refinementList &&
      state.refinementList["star_ingredients.slug"]?.length > 0
    ) {
      if (mainParameter) {
        mainPart += state.refinementList["star_ingredients.slug"][0] + "/"
      }
      for (
        let i = mainParameter ? 1 : 0;
        i < state.refinementList["star_ingredients.slug"].length;
        i++
      ) {
        hashPart.push(`i-${state.refinementList["star_ingredients.slug"][i]}`)
      }
    }
  } else if (
    state.facetsRefinements &&
    state.facetsRefinements["star_ingredients.slug"]?.length
  ) {
    //SSG
    //Only main part because hash are not in ssg
    mainPart = state.facetsRefinements["star_ingredients.slug"][0] + "/"
  }

  const result: ParseReturn = { hashPart: "" }
  if (hashPart.length > 0) {
    result.hashPart = hashPart.join(",")
  }
  if (mainParameter) {
    result.mainPart = mainPart
  }
  return result
}

const getDifficultyPart = (state: SearchState): ParseReturn => {
  const hashPart = []

  if (state.refinementList) {
    if (state.refinementList.difficulty?.length) {
      hashPart.push(`d-${state.refinementList.difficulty}`)
    }
  }

  const result: ParseReturn = { hashPart: "" }
  if (hashPart.length > 0) {
    result.hashPart = hashPart.join(",")
  }
  return result
}

const getCategoriesTreePart = (
  state: SearchState,
  categories: AllCategories,
  mainParameter = false
): ParseReturn => {
  const hashPart = []
  let mainPart = ""

  if (state.hierarchicalMenu) {
    if (
      state.hierarchicalMenu["categories_tree.lvl0"] &&
      state.hierarchicalMenu["categories_tree.lvl0"] !== ""
    ) {
      const categoriesTreeIds =
        state.hierarchicalMenu["categories_tree.lvl0"].split(" > ")
      const lowerCaseLastCategorySlug =
        getCategoryById(
          categoriesTreeIds[categoriesTreeIds.length - 1],
          categories
        )?.slug.toLowerCase() ?? ""

      if (mainParameter) {
        mainPart = lowerCaseLastCategorySlug
      } else {
        hashPart.push(`c-${lowerCaseLastCategorySlug}`)
      }
    }
  }

  const result: ParseReturn = { hashPart: hashPart.join(",") }
  if (mainParameter) {
    result.mainPart = mainPart
  }
  return result
}

const addHashToUrl = (url: string, hashParts: string[]) => {
  hashParts = hashParts.filter(part => !!part)
  if (hashParts.length > 0) {
    return `${url}#${hashParts.join(",")}`
  }
  return url
}

const parseHash = (
  hash: string,
  allCategories: AllCategories,
  baseSearchState: SearchState
): SearchState => {
  const hashParams = hash.slice(1).split(",")

  for (const param of hashParams) {
    const splitedParam = param.split("-")
    const paramEncodedCategory = splitedParam[0]

    //Logic required for light-and-fun vertical
    const paramValue = splitedParam.slice(1, splitedParam.length).join("-")

    const decodedCategoryPath: string = ENCODED_CATEGORIES[paramEncodedCategory]
    if (decodedCategoryPath) {
      const keyList = decodedCategoryPath
        .split(/(\.(?![a-zA-Z]*"])|\["|"])/)
        .filter(part => part) //Remove empty part
        .filter(part => !part.match(/(\.(?![a-zA-Z]+)|\["|"])/)) //Remove . "] and ["
      const lastKey = keyList.splice(-1)[0]
      //If the key doesn't exist, we initialize it with an empty object
      let lastBase: any = baseSearchState
      for (const key of keyList) {
        if (!lastBase[key]) lastBase[key] = {}
        lastBase = lastBase[key]
      }

      if (lastKey === "categories_tree") {
        const categories = getCategoriesFacetsBySlug(paramValue, allCategories)

        if (categories.length > 0) {
          lastBase["categories_tree.lvl0"] = categories.reverse().join(" > ")
        }
      } else if (
        lastKey === "vertical" ||
        lastKey === "star_ingredients.slug" ||
        lastKey === "recipe_type" ||
        lastKey === "suitable_for_diets.slug" ||
        lastKey === "difficulty"
      ) {
        if (!lastBase[lastKey]) {
          lastBase[lastKey] = []
        }
        lastBase[lastKey].push(decodeURI(paramValue))
      }
    }
  }

  return baseSearchState
}

export {
  addHashToUrl,
  ENCODED_CATEGORIES,
  getCategoriesTreePart,
  getDietPart,
  getDifficultyPart,
  getIngredientsPart,
  getTypePart,
  getVerticalPart,
  parseHash,
}
